import { useState, useEffect, useCallback } from "react";

import { Card, CardContent } from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import {
  Loader2,
  CalendarDays,
  AlertTriangle,
} from "lucide-react";

import {
  fetchSchedulingBookings,
  fetchSchedulingSummary,
  fetchRoleCoverage,
  fetchSchedulingWarnings,
} from "@/services/schedulingService";
import {
  mockResourceRequests,
  mockSchedulerConnection,
  type SchedulingSummary,
  type RoleCoverage,
  type SchedulingWarning,
} from "@/lib/mock-scheduling-data";
import { getDataMode } from "@/lib/data-service";

import { useProjectDetailsContext } from "../context";

import ResourcingTabContent from "../components/resourcing-tab-content";

export function ResourcingTab() {
  const ctx = useProjectDetailsContext();
  // Destructure all needed values from context
  const {
    projectId, navigate, setActiveTab,
    enrichedBudgetData,
    schedulingBookings, schedulingLoading,
    schedulingConnectionStatus, schedulingSchedulerName, schedulerUrl, liveResourceRequests,
    resourceRequestsLoading,
    setRaiseRequestModalOpen,
    setRaiseRequestPrefilledRole,
    liveRoleCoverage: contextRoleCoverage,
    setLiveRoleCoverage: setContextRoleCoverage,
    // Additional items that may be used
    setSchedulingBookings, setSchedulingConnectionStatus,
    setSchedulingError, setSchedulingLoading, setSchedulingSchedulerName,
    setSchedulerUrl,
  } = ctx;

  const emptySchedulingSummary: SchedulingSummary = {
    totalBudgetHours: 0, totalScheduledHours: 0, coveragePercent: 0,
    totalBookings: 0, confirmedBookings: 0, plannedBookings: 0,
    unmetRoles: [], bookingsEndDate: '', projectEndDate: '',
    healthStatus: 'FullyResourced', warnings: [],
  };

  const [liveSummary, setLiveSummary] = useState<SchedulingSummary | null>(null);
  const [liveRoleCoverage, setLiveRoleCoverageLocal] = useState<RoleCoverage[] | null>(null);
  const [liveWarnings, setLiveWarnings] = useState<SchedulingWarning[] | null>(null);

  const setLiveRoleCoverage = useCallback((data: RoleCoverage[] | null) => {
    setLiveRoleCoverageLocal(data);
    if (data) setContextRoleCoverage(data);
  }, [setContextRoleCoverage]);

  useEffect(() => {
    if (!projectId || schedulingConnectionStatus !== 'connected') return;
    const dataMode = getDataMode();
    if (dataMode !== 'live') return;

    let cancelled = false;

    async function loadLiveData() {
      try {
        const [summaryResp, coverageResp, warningsResp] = await Promise.all([
          fetchSchedulingSummary(projectId),
          fetchRoleCoverage(projectId),
          fetchSchedulingWarnings(projectId),
        ]);

        if (cancelled) return;

        setLiveSummary({
          totalBudgetHours: summaryResp.totalBudgetHours,
          totalScheduledHours: summaryResp.scheduledHours,
          coveragePercent: summaryResp.bookingCoveragePercent,
          totalBookings: summaryResp.totalBookings,
          confirmedBookings: summaryResp.confirmedBookings,
          plannedBookings: summaryResp.plannedBookings,
          unmetRoles: (summaryResp.unmetRoles ?? []).map((r) => ({
            roleId: r.roleId,
            roleName: r.roleName,
            budgetHours: r.budgetHours,
            scheduledHours: r.scheduledHours,
            gapHours: r.gapHours,
          })),
          bookingsEndDate: summaryResp.bookingsEndDate ?? '',
          projectEndDate: summaryResp.projectEndDate ?? '',
          healthStatus: (summaryResp.healthStatus as SchedulingSummary['healthStatus']) ?? 'NoData',
          warnings: warningsResp,
        });
        setLiveRoleCoverage(coverageResp);
        setLiveWarnings(warningsResp);
      } catch (err) {
        console.error('[ResourcingTab] Error loading live scheduling data:', err);
      }
    }

    loadLiveData();
    return () => { cancelled = true; };
  }, [projectId, schedulingConnectionStatus, schedulingBookings, setLiveRoleCoverage]);

  // Coverage refreshed elsewhere (e.g. after a resource request is raised) lands
  // in the shared context first — mirror it so the table stays in step.
  useEffect(() => {
    if (contextRoleCoverage.length > 0) setLiveRoleCoverageLocal(contextRoleCoverage);
  }, [contextRoleCoverage]);

  return (
    <>
                  {/* Loading state */}
                  {schedulingLoading && (
                    <Card>
                      <CardContent className="py-12">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Loading scheduling data...</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Error state */}
                  {!schedulingLoading && schedulingConnectionStatus === 'error' && (
                    <Card className="border-warning-200 bg-warning-50">
                      <CardContent className="py-8">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <AlertTriangle className="h-8 w-8 text-warning-500" />
                          <p className="text-sm font-medium text-warning-800">
                            Unable to connect to the resource scheduler.
                          </p>
                          <p className="text-xs text-warning-600">
                            Check your integration settings or try again later.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Re-trigger the scheduling data fetch
                              setSchedulingLoading(true);
                              setSchedulingError(null);
                              fetchSchedulingBookings(projectId, { page: 1, pageSize: 1 }).then((response) => {
                                setSchedulingBookings(response.bookings);
                                setSchedulingConnectionStatus(response.connectionStatus);
                                setSchedulingSchedulerName(response.schedulerName);
                                setSchedulerUrl(response.schedulerUrl ?? null);
                              }).catch(() => {
                                setSchedulingConnectionStatus('error');
                                setSchedulingBookings([]);
                              }).finally(() => {
                                setSchedulingLoading(false);
                              });
                            }}
                          >
                            Retry
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Not configured state */}
                  {!schedulingLoading && schedulingConnectionStatus === 'not_configured' && (
                    <Card>
                      <CardContent className="py-12">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <CalendarDays className="h-10 w-10 text-muted-foreground/50" />
                          <div className="text-center">
                            <p className="text-sm font-medium">No Resource Scheduler Connected</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Connect a resource scheduler in Settings &rarr; Integrations to see booking data for this project.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/settings/integrations')}
                          >
                            Go to Integrations
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Connected state — show real data */}
                  {!schedulingLoading && schedulingConnectionStatus === 'connected' && (() => {
                    const dataMode = getDataMode();
                    const isLive = dataMode === 'live';
                    return (
                    <ResourcingTabContent
                      roleCoverage={isLive ? (liveRoleCoverage ?? []) : (liveRoleCoverage ?? [])}
                      warnings={isLive ? (liveWarnings ?? []) : (liveWarnings ?? [])}
                      summary={liveSummary ?? emptySchedulingSummary}
                      schedulerName={schedulingSchedulerName ?? mockSchedulerConnection.displayName}
                      schedulerUrl={schedulerUrl ?? undefined}
                      resourceRequests={isLive ? liveResourceRequests : mockResourceRequests}
                      resourceRequestsLoading={resourceRequestsLoading}
                      hasResourceBudget={Boolean(enrichedBudgetData?.resourceBudget)}
                      onNavigateToResourceBudget={() => setActiveTab('resource-budget')}
                      projectId={projectId}
                      onRaiseRequest={(roleName) => {
                        setRaiseRequestPrefilledRole(roleName);
                        setRaiseRequestModalOpen(true);
                      }}
                    />
                    );
                  })()}

    </>
  );
}
